import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Popover } from 'primeng/popover';

@Component({
  standalone: false,
    selector: 'faro-single-date-input',
    templateUrl: './single-date-input.component.html',
    styleUrls: ['./single-date-input.component.scss'],
})
export class SingleDateInputComponent {
    @Input()
    minDate: Date = new Date('1900-1-1');

    @Input()
    maxDate: Date = new Date('2222-12-31');

    @Input()
    placeholder: string = '';

    @Input()
    set initialDate(date: Date | null) {
        if (date) {
            this.displayDate = date.toLocaleDateString('de-CH');
        }
    }

    @Output()
    dateSelected = new EventEmitter<Date>();

    @ViewChild('singleDateCalendarPanel')
    private _calendarPanel?: Popover;

    displayDate: string | null = '';

    constructor() {}

    onPopoverShow(): void {
        // The p-datePicker wraps its calendar panel in a p-motion component whose
        // @if(rendered()) starts false, so the initial align() sees offsetHeight=0
        // and never flips the popover above the target.  Poll with rAF until the
        // calendar has real layout height, then re-align.
        this._realignWhenCalendarVisible();
    }

    private _realignWhenCalendarVisible(): void {
        const container = this._calendarPanel?.container;
        if (!container) return;

        // Use ResizeObserver to react the moment the calendar grid renders inside the popover.
        // The container starts at ~50px (popover padding + datePicker shell) while the inner
        // p-motion component still hides the calendar grid with display:none.
        // Only once the grid is visible does the height exceed 150px, at which point
        // absolutePosition() has enough height to correctly flip the overlay above the target.
        const observer = new ResizeObserver(() => {
            if ((this._calendarPanel?.container?.offsetHeight ?? 0) > 150) {
                observer.disconnect();
                this._calendarPanel?.align();
            }
        });

        observer.observe(container);

        // Safety valve: disconnect if the popover is closed before the calendar fully renders.
        setTimeout(() => observer.disconnect(), 2000);
    }

    toggleCalendar(event: Event, target: HTMLElement): void {
        if (this._calendarPanel) {
            this._calendarPanel.toggle(event, target);
        }
    }

    clearDate() {
        this.displayDate = '';
        this.dateSelected.emit(new Date());
    }

    onDateSelected(event: Date) {
        this.displayDate = event.toLocaleDateString('de-CH');
        this._calendarPanel?.hide();
        this.dateSelected.emit(event);
    }

    onChange(event: string) {
        const dateformat: RegExp = /^(?<d>(0?[1-9]|[12]\d|3[01]))\.(?<m>(0?[1-9]|1[012]))\.(?<y>(\d{4}))$/;
        if (event.match(dateformat)) {
            const matches = dateformat.exec(event);
            if (matches !== null) {
                const day = matches.groups?.['d'];
                const month = matches.groups?.['m'];
                const year = matches.groups?.['y'];
                if (day && month && year) {
                    this.dateSelected.emit(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
                }
            }
        }
    }
}
